import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import StarRating from '@/components/StarRating';
import BottomNav from '@/components/BottomNav';
import Avatar from '@/components/Avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function FarmerReviewsPage() {
  const { farmerId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();

  const [farmer, setFarmer] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ avg_rating: 0, review_count: 0 });
  const [loading, setLoading] = useState(true);

  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isSelf = user && farmer && user._id === farmerId;
  const canReview = user && !isSelf && user.role !== 'admin';

  const load = useCallback(async () => {
    try {
      const [profileRes, reviewsRes] = await Promise.all([
        api.get(`/farmer/${farmerId}/profile`),
        api.get(`/farmer/${farmerId}/reviews`),
      ]);
      setFarmer(profileRes.data);
      setReviews(reviewsRes.data.reviews || []);
      setSummary({
        avg_rating: reviewsRes.data.avg_rating,
        review_count: reviewsRes.data.review_count,
      });
      if (user) {
        const mine = (reviewsRes.data.reviews || []).find(r => r.reviewer_id === user._id);
        if (mine) {
          setMyRating(mine.rating);
          setMyComment(mine.comment || '');
        }
      }
    } catch {
      toast.error('Could not load reviews');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [api, farmerId, navigate, user]);

  useEffect(() => { load(); }, [load]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!myRating) {
      toast.error('Please select a star rating');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/farmer/${farmerId}/reviews`, {
        rating: myRating,
        comment: myComment,
      });
      toast.success('Review submitted!');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F6F8FB] md:max-w-xl lg:max-w-2xl border-x border-[#E4E8EF] p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-[#EFF2F7] rounded w-1/3" />
          <div className="h-32 bg-[#EFF2F7] rounded-xl" />
          <div className="h-24 bg-[#EFF2F7] rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full max-w-md mx-auto min-h-screen bg-[#F6F8FB] md:max-w-xl lg:max-w-2xl border-x border-[#E4E8EF]"
      data-testid="farmer-reviews-page"
    >
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#F6F8FB]/80 backdrop-blur-xl border-b border-[#E4E8EF] px-4 py-3 flex items-center gap-3">
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-[#EFF2F7] flex items-center justify-center"
        >
          <ArrowLeft className="w-4 h-4 text-[#0B1620]" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold text-[#0B1620] font-['Bricolage_Grotesque'] truncate">
            {farmer?.name ? `${farmer.name}'s Reviews` : 'Reviews'}
          </h1>
        </div>
      </div>

      <div className="p-4 pb-24 space-y-5">
        {/* Summary card */}
        <div
          className="bg-[#F6F8FB] border border-[#E4E8EF] rounded-2xl p-5 text-center"
          data-testid="reviews-summary"
        >
          <p className="text-3xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">
            {summary.review_count > 0 ? summary.avg_rating.toFixed(1) : '–'}
          </p>
          <div className="flex justify-center mt-2">
            <StarRating value={summary.avg_rating} size={20} />
          </div>
          <p className="text-xs text-[#5B6675] mt-2">
            Based on {summary.review_count} {summary.review_count === 1 ? 'review' : 'reviews'}
          </p>
        </div>

        {/* Write a review */}
        {canReview && (
          <form
            onSubmit={submitReview}
            className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 space-y-3"
            data-testid="write-review-form"
          >
            <h3 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">
              {myRating ? 'Update your review' : 'Rate this farmer'}
            </h3>
            <StarRating
              value={myRating}
              size={28}
              interactive
              onChange={setMyRating}
              testId="my-star-input"
            />
            <Textarea
              data-testid="review-comment-input"
              value={myComment}
              onChange={(e) => setMyComment(e.target.value)}
              placeholder="Share your experience (optional)"
              rows={3}
              className="text-sm bg-[#F6F8FB] border-[#E4E8EF] rounded-lg resize-none"
            />
            <Button
              type="submit"
              data-testid="submit-review-btn"
              disabled={submitting}
              className="w-full h-10 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold text-sm"
            >
              {submitting ? 'Submitting…' : 'Submit review'}
            </Button>
          </form>
        )}

        {/* List */}
        <div>
          <h3 className="text-base font-semibold text-[#0B1620] mb-3 font-['Bricolage_Grotesque'] flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#0B4F3F]" />
            All reviews
          </h3>
          {reviews.length === 0 ? (
            <p className="text-sm text-[#5B6675] text-center py-6">
              {canReview ? 'Be the first to leave a review.' : 'No reviews yet.'}
            </p>
          ) : (
            <div className="space-y-3" data-testid="reviews-list">
              {reviews.map((r) => (
                <div key={r.id} className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar name={r.reviewer_name} src={r.reviewer_avatar} size={28} />
                      <span className="text-xs font-medium text-[#0B1620]">{r.reviewer_name}</span>
                    </div>
                    <StarRating value={r.rating} size={12} />
                  </div>
                  {r.comment && <p className="text-sm text-[#5B6675] mt-2">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
